import { useRef } from 'react';
import { Accordion, Form, InputGroup } from 'react-bootstrap';
import { PreferencesTabs, type PreferencesTabsType } from '../Preferences';
import ReactTooltip from 'react-tooltip';
import type { RootState } from '../../../../store';
import { useSelector } from 'react-redux';
import type { PathologyTO } from '../../../../lib';
import { useCheckOverflow } from '../../../helpers/overflowChecker';

interface PathologySettingProps {
  activeTab: PreferencesTabsType;
  pathologyState: Record<string, boolean>;
  onChangePathology: (name: string, value: boolean) => void;
}

interface PathologyItemProps {
  active: boolean;
  index: string;
  pathology: PathologyTO;
  onChangePathology: (name: string, value: boolean) => void;
  pathologyState: Record<string, boolean>;
}

const PathologyItem = ({
  active,
  pathology,
  index,
  onChangePathology,
  pathologyState,
}: PathologyItemProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const isOverflow = useCheckOverflow(spanRef, active);
  const tooltipId = `tooltip-${pathology.id}`;

  return (
    <>
      <InputGroup className="mb-3 pathology-item">
        <InputGroup.Checkbox
          name={`${pathology.id}`}
          checked={!!pathologyState[`${pathology.id}`]}
          onChange={(e) => onChangePathology(e.target.name, e.target.checked)}
        />
        <span
          ref={spanRef}
          id={'pathology-box' + index}
          className="pathology-box"
          data-for={tooltipId}
          data-tip
          style={{
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {pathology.name}
        </span>
      </InputGroup>
      {isOverflow && (
        <ReactTooltip id={tooltipId} effect="solid" key={tooltipId}>
          <div className="mb-1">{pathology.name}</div>
        </ReactTooltip>
      )}
    </>
  );
};

const PathologySetting = ({
  activeTab,
  pathologyState,
  onChangePathology,
}: PathologySettingProps) => {
  const { allPathologies } = useSelector(
    (state: RootState) => state.preferences,
  );
  const active = activeTab === PreferencesTabs.PATHOLOGY;

  return (
    <Accordion.Item eventKey={PreferencesTabs.PATHOLOGY}>
      <Accordion.Header>
        <span className="d-flex align-items-center font-weight-600 gap-3">
          Pathology
        </span>
        {!active ? (
          <i className="fa fa-plus" aria-hidden="true" />
        ) : (
          <i className="fa fa-minus" aria-hidden="true" />
        )}
      </Accordion.Header>
      <Accordion.Body>
        <div className="list-information-header">
          Unselect any pathology results you don’t want to see on your patient
          timelines
        </div>
        <Form className="my-4 pathology-list-container">
          {allPathologies?.map((pathology: PathologyTO) => (
            <PathologyItem
              active={active}
              pathologyState={pathologyState}
              pathology={pathology}
              onChangePathology={onChangePathology}
              key={pathology.id}
              index={pathology.id}
            />
          ))}
        </Form>
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default PathologySetting;
